import { formatCurrency } from './l10n';

export const DYNAMIC_PRICE_TIER_STYLES = {
  value: {
    key: 'value',
    color: '#1FA36B',
    textColor: '#00805F',
    backgroundColor: '#E9F7F2',
  },
  standard: {
    key: 'standard',
    color: '#F2B600',
    textColor: '#CD8701',
    backgroundColor: '#FFF8E3',
  },
  peak: {
    key: 'peak',
    color: '#F2675F',
    textColor: '#FA4646',
    backgroundColor: '#FDEDEC',
  },
};

export const SOLD_OUT_PRICE_COLOR = '#B8C6D9';

export const CALENDAR_DATE_STATUS = {
  AVAILABLE: 'available',
  CLOSED: 'closed',
  SOLD_OUT: 'sold-out',
  NOT_OFFERED: 'not-offered',
};

export const getCalendarPriceCents = ({ allAccessPrice, minPrice } = {}) =>
  Number.isFinite(allAccessPrice)
    ? allAccessPrice
    : Number.isFinite(minPrice)
    ? minPrice
    : null;

export const getCalendarDateStatus = ({
  calendarPricing,
  configuredUnavailable,
} = {}) => {
  if (configuredUnavailable || calendarPricing?.closed)
    return CALENDAR_DATE_STATUS.CLOSED;
  if (calendarPricing?.notOffered) return CALENDAR_DATE_STATUS.NOT_OFFERED;
  if (calendarPricing?.soldOut) return CALENDAR_DATE_STATUS.SOLD_OUT;
  return CALENDAR_DATE_STATUS.AVAILABLE;
};

export const isCalendarDateUnavailable = (options = {}) =>
  getCalendarDateStatus(options) !== CALENDAR_DATE_STATUS.AVAILABLE;

export const hasAvailableTicketQuantity = (item) => {
  const quantity = item?.quantity;
  const groupSize = item?.groupSize || 1;

  if (typeof quantity === 'number') return quantity >= groupSize;
  if (quantity && typeof quantity === 'object') {
    return Object.values(quantity).some((value) => Number(value) >= groupSize);
  }

  return false;
};

export const isTicketInventoryUnavailable = (inventory) =>
  !inventory?.length ||
  inventory.every((item) => !hasAvailableTicketQuantity(item));

const getUniqueAvailablePrices = (datePricing) =>
  Array.from(
    new Set(
      Object.values(datePricing || {})
        .filter(
          (pricing) =>
            pricing &&
            !pricing.closed &&
            !pricing.soldOut &&
            !pricing.notOffered &&
            Number.isFinite(pricing.priceCents)
        )
        .map(({ priceCents }) => priceCents)
    )
  ).sort((left, right) => left - right);

const createTier = (key, prices) => ({
  ...DYNAMIC_PRICE_TIER_STYLES[key],
  prices,
  minPriceCents: prices[0],
  maxPriceCents: prices[prices.length - 1],
  label: `${formatCurrency(prices[0])}${prices.length > 1 ? '+' : ''}`,
});

export const buildDynamicPriceTiers = (datePricing) => {
  const prices = getUniqueAvailablePrices(datePricing);

  if (!prices.length) return [];
  if (prices.length === 1) return [createTier('value', prices)];
  if (prices.length === 2) {
    return [
      createTier('value', prices.slice(0, 1)),
      createTier('peak', prices.slice(1)),
    ];
  }
  if (prices.length === 3) {
    return [
      createTier('value', prices.slice(0, 1)),
      createTier('standard', prices.slice(1, 2)),
      createTier('peak', prices.slice(2)),
    ];
  }

  return [
    createTier('value', prices.slice(0, 1)),
    createTier('standard', prices.slice(1, -1)),
    createTier('peak', prices.slice(-1)),
  ];
};

export const buildDynamicCalendarPricing = (datePricing) => {
  const tiers = buildDynamicPriceTiers(datePricing);
  const tierByPrice = tiers.reduce((result, tier) => {
    tier.prices.forEach((price) => {
      result[price] = tier;
    });
    return result;
  }, {});

  const dates = Object.entries(datePricing || {}).reduce(
    (result, [date, pricing]) => {
      if (!pricing) return result;

      if (pricing.closed) {
        result[date] = {
          ...pricing,
          tier: 'closed',
          color: SOLD_OUT_PRICE_COLOR,
        };
        return result;
      }

      if (pricing.soldOut) {
        result[date] = {
          ...pricing,
          tier: 'sold-out',
          color: SOLD_OUT_PRICE_COLOR,
        };
        return result;
      }

      if (pricing.notOffered) {
        result[date] = {
          ...pricing,
          tier: CALENDAR_DATE_STATUS.NOT_OFFERED,
          color: SOLD_OUT_PRICE_COLOR,
        };
        return result;
      }

      const tier = tierByPrice[pricing.priceCents];
      result[date] = {
        ...pricing,
        tier: tier?.key || null,
        color: tier?.color || null,
      };
      return result;
    },
    {}
  );

  return {
    dates,
    legendTypes: tiers.map(
      ({
        key,
        label,
        color,
        textColor,
        backgroundColor,
        minPriceCents,
        maxPriceCents,
      }) => ({
        key,
        label,
        color,
        textColor,
        backgroundColor,
        minPriceCents,
        maxPriceCents,
      })
    ),
  };
};

export const getCalendarPriceLabel = (pricing) => {
  if (Number.isFinite(pricing?.priceCents)) {
    return formatCurrency(pricing.priceCents);
  }

  // Compatibility for calendar consumers that have not migrated to cents yet.
  return Number.isFinite(pricing?.price) ? `$${pricing.price}` : null;
};
